import { getAuthHeaders } from '@/identity.js';
import type { SharePointAttachmentRef } from '@/integration/types.js';

/**
 * Where the import route lives.
 *
 * NOT `/v1/workspaces/:id/files`, however much it reads like it belongs there. The edge routes by static
 * prefix, and `/v1/workspaces` belongs to `cti-workspace-service` — which knows nothing about SharePoint
 * credentials or streaming bytes. `/v1/workspace-artifacts` is the prefix that reaches
 * `cti-conversation-service`, where the importer actually is, so the address of this route is a routing
 * fact and not a naming preference. Send it to the pretty path and it 404s at the edge.
 */
const IMPORT_BASE = '/v1/workspace-artifacts';

/** The artifact an import created, as the route reports it. The storage key is never among these. */
export interface ImportedWorkspaceFile {
  artifactId: string;
  name: string;
  contentType: string;
  size: number;
  /** Where the copy came from, when the answer named it. */
  webUrl?: string;
  importedAt: string;
}

/**
 * Copy one SharePoint document into the workspace's files (`POST /v1/workspace-artifacts/:id/files`).
 *
 * The bytes move: what comes back is a normal workspace artifact with an id of ours, which is the whole
 * point — an automation fires with nobody at the screen, so it cannot be handed a reference that only
 * resolves through somebody's live SharePoint session.
 *
 * Only the ADDRESS travels, in the wire's own snake_case. `name` is deliberately not forwarded: the
 * server reads the real name from the document, and a field the DTO does not declare is dropped by the
 * validation pipe anyway — silently, which is exactly how a wire mismatch hides.
 *
 * A refusal is thrown, never swallowed: a file that did not arrive must not read like one that did — and
 * an answer that named no artifact is one of those refusals, whatever its status code said.
 */
export async function putFileInWorkspace(
  workspaceId: string,
  address: SharePointAttachmentRef,
): Promise<ImportedWorkspaceFile> {
  const res = await fetch(
    `${IMPORT_BASE}/${encodeURIComponent(workspaceId)}/files`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        ...(address.connection_id
          ? { connection_id: address.connection_id }
          : {}),
        ...(address.site_id ? { site_id: address.site_id } : {}),
        drive_id: address.drive_id,
        item_id: address.item_id,
        ...(address.web_url ? { web_url: address.web_url } : {}),
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      importFailureMessage(detail, res.status, address.name || 'this file'),
    );
  }
  const body = (await res.json()) as {
    artifact_id?: string;
    name?: string;
    content_type?: string;
    size?: number;
    web_url?: string | null;
    imported_at?: string;
  };
  /**
   * The one field this answer cannot be vague about. The id is the whole reason the bytes moved: it is
   * what an automation resolves on its own, hours later, with no session to borrow. So a 2xx that named
   * none is a failed import and is thrown like any other refusal — an empty `artifact_id` would put a
   * chip on the screen now and resolve to nothing on the run. The name and the type are only that chip's
   * caption, so they stay soft.
   */
  const artifactId =
    typeof body.artifact_id === 'string' ? body.artifact_id.trim() : '';
  if (!artifactId) {
    throw new Error(
      importFailureMessage('', res.status, address.name || 'this file'),
    );
  }
  return {
    artifactId,
    name: body.name ?? '',
    contentType: body.content_type ?? '',
    size: typeof body.size === 'number' ? body.size : 0,
    ...(body.web_url ? { webUrl: body.web_url } : {}),
    importedAt: body.imported_at ?? '',
  };
}

/**
 * The server's own words when it sent any, and a readable fallback when it sent none.
 *
 * `message` is read as a LIST as well as a string, the way every neighbouring api module reads it: a
 * NestJS `ValidationPipe` refusal arrives as `{ message: string[] }`, and taking only the string shape
 * threw away the one thing that says WHY the file was refused — which is the whole reason this route
 * raises instead of skipping quietly.
 */
function importFailureMessage(
  detail: string,
  status: number,
  fileName: string,
): string {
  try {
    const body = JSON.parse(detail) as { message?: string | string[] };
    if (Array.isArray(body.message) && body.message.length > 0) {
      return body.message.join('; ');
    }
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  } catch {
    if (detail.trim()) return detail.trim();
  }
  return `Could not add “${fileName}” to this workspace (${status})`;
}
